/**
 * Member API Service
 *
 * Centralized fetch wrapper for the member/customer portal that surfaces
 * 401 Unauthorized responses through a `member:session-expired` window
 * event. CustomerPortal listens for that event and renders a modal
 * prompting re-login, instead of letting the UI silently break when a
 * token expires mid-session (OWZ-13).
 *
 * Mirrors the adminApi pattern but with event-driven UX (no logout
 * callback registration needed — any caller can dispatch).
 */

import { markMutationSuccess, reportMutationFailure } from './connectivityService';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const LOGIN_GRACE_PERIOD_MS = 3000;
let lastLoginTimestamp = 0;

export const markMemberLoginSuccess = () => {
  lastLoginTimestamp = Date.now();
};

// Test-only escape hatch — module-level state means the grace period flag
// would otherwise leak across tests in the same suite.
export const __resetMemberApiForTests = () => {
  lastLoginTimestamp = 0;
};

const isWithinLoginGracePeriod = () =>
  Date.now() - lastLoginTimestamp < LOGIN_GRACE_PERIOD_MS;

export const getMemberToken = () =>
  localStorage.getItem('memberToken') || localStorage.getItem('customerToken');

export const getMemberAuthHeaders = (isJson = true) => {
  const headers = {
    'Authorization': `Bearer ${getMemberToken() || ''}`,
  };
  if (isJson) headers['Content-Type'] = 'application/json';
  return headers;
};

/**
 * Dispatch the session-expired event so CustomerPortal (or anything
 * else that cares) can prompt the user to re-login. Safe to call from
 * any fetch-using code path.
 */
export const reportMemberSessionExpired = (reason = 'expired') => {
  if (isWithinLoginGracePeriod()) return;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('member:session-expired', { detail: { reason } })
  );
};

/**
 * Fetch wrapper that auto-attaches the member auth header and reports
 * 401 responses as session-expired. Returns the raw Response so callers
 * can branch on status, parse JSON, etc.
 */
export const memberFetch = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
  const method = String(options.method || 'GET').toUpperCase();
  const isMutation = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  if (isMutation && typeof navigator !== 'undefined' && navigator.onLine === false) {
    reportMutationFailure();
    return new Response(JSON.stringify({ detail: 'You are offline. Reconnect to take action.' }), {
      status: 503,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  const isFormData = options.body instanceof FormData;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...getMemberAuthHeaders(!isFormData),
      ...options.headers,
    },
  });

  if (isMutation && response.ok) markMutationSuccess();
  if (isMutation && !response.ok && response.status === 0) reportMutationFailure();

  if (response.status === 401) {
    let reason = 'expired';
    try {
      const data = await response.clone().json();
      if (data?.detail && String(data.detail).toLowerCase().includes('inactiv')) {
        reason = 'inactivity';
      }
    } catch {
      /* response had no JSON body */
    }
    reportMemberSessionExpired(reason);
  }

  return response;
};
